import { getLang } from './util/i18n.js'
import { getPartnersTexts } from './data/partnersTexts.js'

// 预解析伙伴logo，确保Vite构建时正确拷贝静态资源
const partnerLogos = {
  bongroup: new URL('../assets/svg/partner-bongroup-b.svg', import.meta.url)
    .href,
  codere: new URL('../assets/svg/partner-codere-b.svg', import.meta.url).href,
  pyradia: new URL('../assets/svg/partner-pyradia-b.svg', import.meta.url).href,
  hemo: new URL('../assets/svg/partner-hemo-b.svg', import.meta.url).href,
  lpw: new URL('../assets/svg/partner-LPW-b.svg', import.meta.url).href,
  mecanolav: new URL('../assets/svg/partner-mecanolav-b.svg', import.meta.url)
    .href
}

/**
 * Initialize the interactive partner showcase on the home page.
 */
export function initPartnersShowcase() {
  const section = document.querySelector('[data-partners]')
  if (!section) return
  const shouldScrollToCenterOnMobile =
    section.dataset.mobileScrollCenter !== 'false'

  const lang = getLang()
  const t = getPartnersTexts(lang)

  // Generate partners data from texts
  // Order of keys in partnersTexts.items should be preserved or we can specify order if needed.
  // The keys in partnersTexts.items are: bongroup, codere, pyradia, hemo, lpw, mecanolav
  const keys = ['bongroup', 'codere', 'pyradia', 'hemo', 'lpw', 'mecanolav']

  const partnersData = keys.map((key) => {
    const item = t.items[key]
    return {
      id: key,
      name: item.name,
      tagline: item.desc, // mapped from 'desc' in texts
      description: item.description, // mapped from 'description' in texts
      strengths: item.strengths,
      metrics: [
        {
          label: t.headings.region,
          value: item.metrics.region
        },
        {
          label: t.headings.focus,
          value: item.metrics.focus
        },
        {
          label: t.headings.since,
          value: item.metrics.since
        }
      ],
      image: partnerLogos[key],
      link: item.link
    }
  })

  const partnersMap = new Map(
    partnersData.map((partner) => [partner.id, partner])
  )

  const cards = Array.from(section.querySelectorAll('[data-partner-card]'))
  const detailEl = section.querySelector('[data-partner-detail]')

  if (!cards.length || !detailEl) return

  const logoEl = detailEl.querySelector('[data-logo]')
  const titleEl = detailEl.querySelector('[data-title]')
  const subtitleEl = detailEl.querySelector('[data-subtitle]')
  const descriptionEl = detailEl.querySelector('[data-description]')
  const chipsWrap = detailEl.querySelector('[data-chips]')
  const metricsWrap = detailEl.querySelector('[data-metrics]')
  // ctaEl might be static in HTML but we also dynamically create one below
  const ctaEl = detailEl.querySelector('[data-cta]')

  let activeId =
    cards.find((card) => card.dataset.active === 'true').dataset.partnerCard ??
    partnersData[0].id

  if (!partnersMap.has(activeId) && partnersData.length) {
    activeId = partnersData[0].id
  }

  const updateDetail = (id) => {
    const partner = partnersMap.get(id)
    if (!partner) return

    if (logoEl) {
      logoEl.src = partner.image
      logoEl.alt = `${partner.name} logo`
    }
    if (titleEl) titleEl.textContent = partner.name
    if (subtitleEl) subtitleEl.textContent = partner.tagline
    if (descriptionEl) descriptionEl.textContent = partner.description

    if (chipsWrap) {
      chipsWrap.innerHTML = ''
      partner.strengths.forEach((strength) => {
        const chip = document.createElement('span')
        chip.className =
          'rounded-full bg-white/10 px-3 py-2 text-[0.65rem] font-semibold uppercase tracking-[0.2em] sm:px-4 sm:text-xs sm:tracking-[0.25em]'
        chip.textContent = strength
        chipsWrap.appendChild(chip)
      })
    }

    if (metricsWrap) {
      metricsWrap.innerHTML = ''
      partner.metrics.forEach((item) => {
        const wrapper = document.createElement('div')
        wrapper.className = 'space-y-1'

        const dt = document.createElement('dt')
        dt.className =
          'text-[0.65rem] uppercase tracking-[0.35em] text-white/60 sm:text-xs sm:tracking-[0.45em]'
        dt.textContent = item.label

        const dd = document.createElement('dd')
        dd.className = 'text-base font-semibold text-white sm:text-xl'
        dd.textContent = item.value

        wrapper.append(dt, dd)
        metricsWrap.appendChild(wrapper)
      })

      // 添加 CTA 链接作为第四个网格项
      const ctaWrapper = document.createElement('div')
      ctaWrapper.className = 'flex items-end'

      const ctaLink = document.createElement('a')
      ctaLink.href = partner.link
      ctaLink.setAttribute('data-cta', '')
      ctaLink.setAttribute('aria-label', `了解 ${partner.name} 的合作能力`)
      ctaLink.className =
        'inline-flex w-full items-center justify-center gap-2 rounded-full border border-white/30 px-5 py-3 text-[0.65rem] font-semibold tracking-[0.3em] text-white uppercase transition-colors duration-300 hover:border-white hover:bg-white hover:text-[#010044] sm:w-auto sm:px-6 sm:text-xs sm:tracking-[0.45em]'
      ctaLink.innerHTML = `${t.cta} <span aria-hidden>→</span>`

      ctaWrapper.appendChild(ctaLink)
      metricsWrap.appendChild(ctaWrapper)
    }

    if (ctaEl) {
      ctaEl.href = partner.link
      ctaEl.setAttribute('aria-label', `了解 ${partner.name} 的合作能力`)
      // Also update text for the static CTA if it wasn't replaced by the dynamic metricsWrap one
      // However, the original code appended a NEW link into metricsWrap, avoiding ctaEl?
      // Actually metricsWrap contains the DL list. The original HTML had the CTA *inside* the DL (metricsWrap).
      // So wiping metricsWrap wipes the static CTA.
      // The code re-creates it. Perfect.
    }
  }

  const setActive = (id) => {
    cards.forEach((card) => {
      const isActive = card.dataset.partnerCard === id
      card.dataset.active = isActive ? 'true' : 'false'
      card.setAttribute('aria-pressed', String(isActive))
    })
  }

  const scrollDetailToCenterOnMobile = () => {
    if (!shouldScrollToCenterOnMobile) return
    // Tailwind 的 lg 断点是 1024px，只在移动端/平板窄屏触发
    if (!window.matchMedia('(max-width: 1023px)').matches) return

    const detailRect = detailEl.getBoundingClientRect()
    const targetTop =
      window.scrollY +
      detailRect.top -
      (window.innerHeight / 2 - detailRect.height / 2)

    window.scrollTo({
      top: Math.max(0, targetTop),
      behavior: 'smooth'
    })
  }

  const handleSelect = (id) => {
    if (!id || id === activeId) return
    activeId = id
    setActive(activeId)
    updateDetail(activeId)
    requestAnimationFrame(scrollDetailToCenterOnMobile)
  }

  cards.forEach((card) => {
    const id = card.dataset.partnerCard
    card.addEventListener('mouseenter', () => handleSelect(id))
    card.addEventListener('focus', () => handleSelect(id))
    card.addEventListener('click', () => handleSelect(id))
  })

  // Initial update to ensure content matches language (even if static HTML was correct, this ensures consistency)
  setActive(activeId)
  updateDetail(activeId)
}
